import { LabelBarcode } from './LabelTemplate';

/**
 * Renders a saved label template's free-form elements against real label data.
 * Coordinates are in mm (label reading orientation). Root uses the `.ql-label`
 * class + mm size so the print pipeline (LabelPrintDialog.doPrint) picks it up
 * and applies the same roll-matching / rotation logic as the auto layout.
 * `scale` (px per mm) is only used for the on-screen editor preview.
 */
export function renderElementContent(el, data) {
  const values = data?.values || {};
  const barcode = data?.barcode || '';
  if (el.type === 'barcode') {
    return <LabelBarcode value={barcode} format="code128" />;
  }
  if (el.type === 'qr') {
    return <LabelBarcode value={barcode} format="qr" />;
  }
  if (el.type === 'line' || el.type === 'box') return null;
  // text / field
  const raw = el.type === 'text' ? (el.text || '') : (el.field === 'barcode' ? barcode : (values[el.field] ?? ''));
  const prefix = el.type === 'field' && el.showLabel ? `${el.labelText || ''}: ` : '';
  return (
    <span style={{
      overflow: 'hidden', width: '100%',
      textOverflow: el.wrap ? 'clip' : 'ellipsis',
    }}>{prefix}{String(raw)}</span>
  );
}

export function elementBoxStyle(el, scale = 1) {
  const u = (mm) => (scale === 1 ? `${mm}mm` : `${mm * scale}px`);
  const base = {
    position: 'absolute', left: u(el.x || 0), top: u(el.y || 0),
    width: u(el.w || 10), height: u(el.h || 6), overflow: 'hidden', boxSizing: 'border-box',
  };
  if (el.type === 'line') return { ...base, height: u(el.h || 0.4), background: '#000' };
  if (el.type === 'box') return { ...base, border: `${scale === 1 ? `${el.lineWidth || 0.3}mm` : `${(el.lineWidth || 0.3) * scale}px`} solid #000`, background: 'transparent' };
  if (el.type === 'barcode' || el.type === 'qr') {
    return { ...base, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  }
  return {
    ...base, display: 'flex', alignItems: el.wrap ? 'flex-start' : 'center',
    justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start',
    fontSize: scale === 1 ? `${el.fontSize || 3}mm` : `${(el.fontSize || 3) * scale}px`,
    fontWeight: el.bold ? 700 : 400, lineHeight: 1.1,
    whiteSpace: el.wrap ? 'pre-wrap' : 'nowrap', wordBreak: el.wrap ? 'break-word' : 'normal',
    textAlign: el.align || 'left', color: '#000',
  };
}

export function LabelCanvas({ template, data, w, h }) {
  return (
    <div className="ql-label" style={{ width: `${w}mm`, height: h ? `${h}mm` : 'auto', minHeight: h ? undefined : '16mm', position: 'relative', overflow: 'hidden' }}>
      {(template?.elements || []).map((el) => {
        const cls = el.type === 'qr' ? 'ql-qr' : el.type === 'barcode' ? 'ql-bc' : undefined;
        return (
          <div key={el.id} className={cls} style={elementBoxStyle(el, 1)}>
            {renderElementContent(el, data)}
          </div>
        );
      })}
    </div>
  );
}
